import React, {useEffect, useState} from 'react'
import StayCard from './StayCard'
import {useGetMemberReservationsQuery} from '../../services/guestPortalApi'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {Button, Modal} from 'react-daisyui'
import AddOTAReservation from './AddOTAReservation'
import StaysEmptyState from '../../assets/stays-empty-state.png'
import {detectAndTrackBookNow} from '../../services/funnelAnalytics'
import {useAppSelector} from '../../store/hooks'
import {selectUser} from '../../store/userSlice'

const MemberStays = ({onManageStay}: {onManageStay: (stay: any) => void}) => {
  const user = useAppSelector(selectUser)
  const staysQuery = useGetMemberReservationsQuery({
    reservationStatus: 'Confirmed,InHouse'
  })

  const canceledStaysQuery = useGetMemberReservationsQuery({
    reservationStatus: 'Canceled,NoShow',
    sortOrder: 'desc'
  })
  const pastStaysQuery = useGetMemberReservationsQuery({
    reservationStatus: 'CheckedOut',
    sortOrder: 'desc'
  })

  const {Dialog, handleShow} = Modal.useDialog()
  const [
    showAddOTAReservationDialog,
    setshowAddOTAReservationDialog
  ] = useState(false)

  useEffect(() => {}, [staysQuery, canceledStaysQuery, pastStaysQuery])

  const activeStays = staysQuery.data || []
  const pastStays = pastStaysQuery.data || []
  const canceledStays = canceledStaysQuery.data || []

  return (
    <main className="rows gap-4 px-4 md:px-0" aria-label="Member Stays Section">
      <Dialog
        open={showAddOTAReservationDialog}
        onClose={() => setshowAddOTAReservationDialog(false)}
        className="modal-box bg-white max-w-sm"
        aria-labelledby="add-ota-reservation-dialog-title"
        aria-describedby="add-ota-reservation-dialog-description"
      >
        <h2 id="add-ota-reservation-dialog-title" className="sr-only">
          Add OTA Reservation
        </h2>
        <p id="add-ota-reservation-dialog-description" className="sr-only">
          Use this form to add a reservation made through a third-party service.
        </p>
        <AddOTAReservation
          onSignUpSuccess={() => {
            setshowAddOTAReservationDialog(false)
            staysQuery.refetch()
            canceledStaysQuery.refetch()
            pastStaysQuery.refetch()
          }}
          aria-label="Add OTA Reservation Form"
        ></AddOTAReservation>
        <form method="dialog" aria-labelledby="cancel-button-description">
          <Button
            className="btn btn-link no-underline text-sm w-full"
            id="cancel-button-description"
            aria-label="Cancel and close the dialog"
          >
            cancel
          </Button>
        </form>
      </Dialog>

      <div className="grid grid-cols-1 gap-6">
        <div>
          {staysQuery.isLoading && (
            <div
              className="flex flex-row justify-center items-center pt-4 gap-2"
              aria-live="polite"
              aria-busy="true"
            >
              <LoadingSpinner size={LoadingSpinnerSize.Large} />
              <span className="sr-only">Loading active reservations...</span>
            </div>
          )}

          {!staysQuery.isLoading && activeStays.length === 0 && (
            <div
              className="flex flex-col items-center gap-4 mb-8"
              aria-live="polite"
            >
              <img
                src={StaysEmptyState}
                className="h-52 my-8 mx-auto"
                alt="No stays available"
              />
              <h3 className="text-lg text-center font-bold">
                You don't have any stays coming up.
              </h3>
              <Button
                className="center btn-sm lowercase text-center"
                color="secondary"
                onClick={event => {
                  // Track book now click for first-time user journey
                  const target = event.currentTarget as HTMLElement
                  const bookingUrl =
                    'https://www.placemakr.com/book#/booking/search'

                  console.log('📱 [MemberStays] Book now button clicked!', {
                    userId: (user as any)?.email,
                    url: bookingUrl
                  })

                  // Try to track the book now click
                  const tracked = detectAndTrackBookNow(
                    target,
                    bookingUrl,
                    (user as any)?.email
                  )
                  console.log(
                    '📱 [MemberStays] Book now tracking result:',
                    tracked
                  )

                  // Open the booking page
                  window.open(bookingUrl)
                }}
                aria-label="Book a stay now"
              >
                book now
              </Button>
            </div>
          )}

          {activeStays.length === 0 &&
            (canceledStays.length > 0 || pastStays.length > 0) && (
              <div className="w-full h-px bg-gray-300 mt-6 mb-2" />
            )}

          {activeStays.length > 0 && (
            <div>
              <h1
                className="text-2xl font-semibold py-4"
                id="active-reservations-title"
              >
                Active Reservations
              </h1>
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                aria-labelledby="active-reservations-title"
              >
                {activeStays.map((stay: any, index: number) => (
                  <div
                    key={`stay-${stay.reservationId}`}
                    className="max-w-[500px]"
                    aria-label={`Reservation ${index + 1}`}
                  >
                    <StayCard
                      stay={stay}
                      viewReservation={true}
                      onManageStay={onManageStay}
                    ></StayCard>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          {pastStays.length > 0 && (
            <div>
              {/* Divisory line between active and past reservations */}
              {activeStays.length > 0 && (
                <div className="w-full h-px bg-gray-300 my-8" />
              )}
              <h1
                className="text-2xl font-semibold py-4"
                id="past-reservations-title"
              >
                Past Reservations
              </h1>
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                aria-labelledby="past-reservations-title"
              >
                {pastStays.map((stay: any, index: number) => (
                  <div
                    key={`stay-${stay.reservationId}`}
                    className="max-w-[500px]"
                    aria-label={`Past reservation ${index + 1}`}
                  >
                    <StayCard
                      stay={{...stay, isPastReservation: true}}
                      viewReservation={true}
                      onManageStay={onManageStay}
                    ></StayCard>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          {canceledStays.length > 0 && (
            <div>
              {/* Divisory line between previous sections and cancelled reservations */}
              {(activeStays.length > 0 || pastStays.length > 0) && (
                <div className="w-full h-px bg-gray-300 my-8" />
              )}
              <h1
                className="text-2xl font-semibold pt-0 pb-4"
                id="cancelled-reservations-title"
              >
                Cancelled Reservations
              </h1>
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                aria-labelledby="cancelled-reservations-title"
              >
                {canceledStays.map((stay: any, index: number) => (
                  <div
                    key={`stay-${stay.reservationId}`}
                    className="max-w-[500px]"
                    aria-label={`Cancelled reservation ${index + 1}`}
                  >
                    <StayCard
                      stay={{...stay, isPastReservation: true}}
                      viewReservation={false}
                      onManageStay={onManageStay}
                    ></StayCard>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

export default MemberStays
